import { RecipeCard } from "@/components/recipes/RecipeCard";
import { RecipeCardList } from "@/components/recipes/RecipeCardList";
import { usePageBreadcrumbs } from "@/components/layout/breadcrumb-context";
import { RecipeSearchBar } from "@/components/recipes/RecipeSearchBar";
import { AdvancedFilters } from "@/components/recipes/AdvancedFilters";
import { Skeleton } from "@/components/ui/skeleton";
import { ViewToggle } from "@/components/ui/view-toggle";
import { useAuthProfile } from "@/hooks/use-auth-profile";
import { useRecipeBoxes } from "@/hooks/use-recipe-boxes";
import { useAllRecipes } from "@/hooks/use-all-recipes";
import { useViewPreference } from "@/hooks/use-view-preference";
import { searchRecipes, type SearchFilters } from "@/lib/recipe-search-service";
import { addToSearchHistory } from "@/lib/search-history-service";
import { Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

export default function SearchPage() {
  const { user } = useAuthProfile();
  const { boxes } = useRecipeBoxes(user?.uid);
  const { recipes, loading } = useAllRecipes(boxes);
  const { viewPreference, setViewPreference } = useViewPreference();
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState<SearchFilters>({});

  usePageBreadcrumbs([{ label: "Recipe Boxes", href: "/home" }, { label: "Search" }]);

  const searchResults = useMemo(() => {
    if (!searchQuery.trim() && Object.keys(filters).length === 0) {
      return [];
    }

    return searchRecipes(recipes, searchQuery, filters, {
      scope: "all",
    });
  }, [recipes, searchQuery, filters]);

  // Save to search history when search is performed
  useEffect(() => {
    if (searchQuery.trim() || Object.keys(filters).length > 0) {
      addToSearchHistory(searchQuery, filters as Record<string, unknown>);
    }
  }, [searchQuery, filters]);

  const handleFiltersChange = (newFilters: SearchFilters) => {
    setFilters(newFilters);
  };

  const handleResetFilters = () => {
    setFilters({});
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-2">
        <h1 className="font-headline text-2xl font-semibold">Search</h1>
        <div className="ml-auto">
          <ViewToggle view={viewPreference} onViewChange={setViewPreference} />
        </div>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="flex-1">
          <RecipeSearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search across all your recipe boxes..."
            showHistory={true}
          />
        </div>
        <AdvancedFilters
          recipes={recipes}
          recipeBox={null}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onReset={handleResetFilters}
        />
      </div>

      {searchQuery && (
        <p className="text-sm text-muted-foreground">
          Found {searchResults.length} recipe{searchResults.length !== 1 ? "s" : ""}
        </p>
      )}

      {loading ? (
        viewPreference === "card" ? (
          <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(9rem, 1fr))" }}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="aspect-square w-full rounded-lg" />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-24 w-full rounded-lg" />
            ))}
          </div>
        )
      ) : searchQuery || Object.keys(filters).length > 0 ? (
        searchResults.length > 0 ? (
          viewPreference === "card" ? (
            <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(9rem, 1fr))" }}>
              {searchResults.map((recipe) => (
                <RecipeCard key={recipe.id} recipe={recipe} userId={user?.uid ?? null} />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {searchResults.map((recipe) => (
                <RecipeCardList key={recipe.id} recipe={recipe} userId={user?.uid ?? null} />
              ))}
            </div>
          )
        ) : (
          <div className="rounded-lg border border-dashed p-6 text-center">
            <Search className="mx-auto h-12 w-12 text-muted-foreground" />
            <p className="mt-4 font-headline text-lg">No recipes found</p>
            <p className="mt-2 text-sm text-muted-foreground">
              Try adjusting your search query or filters.
            </p>
          </div>
        )
      ) : (
        <div className="rounded-lg border border-dashed p-6 text-center">
          <Search className="mx-auto h-12 w-12 text-muted-foreground" />
          <p className="mt-4 font-headline text-lg">Start searching</p>
          <p className="mt-2 text-sm text-muted-foreground">
            Enter a search query above to find recipes across all your recipe boxes.
          </p>
        </div>
      )}
    </div>
  );
}

